/**
 * RevokePermissionModal Component
 *
 * Confirmation modal for revoking a permission from a role assignment.
 */

import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useRevokePermission } from '@/lib/hooks/usePermissions';
import { type PermissionsGrant } from '@pillar-portal/api-types';
import { useEffect } from 'react';
import { Role } from '@pillar-portal/database/dist/frontend';
import { PermissionGrantSummary } from '@/components/permissions/PermissionGrantSummary';

interface RevokePermissionModalProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    roleAssignmentUuid: string;
    permission: PermissionsGrant | null;
    role: Role;
}

export function RevokePermissionModal({
    open,
    onOpenChange,
    roleAssignmentUuid,
    permission,
    role,
}: RevokePermissionModalProps) {
    const revokeMutation = useRevokePermission();

    // Reset mutation when modal opens to ensure clean state
    // Don't reset on close - React Query handles cleanup on unmount
    // Resetting during unmount/close causes React error #185 (updating unmounted component)
    useEffect(() => {
        if (open) {
            revokeMutation.reset();
        }
        // revokeMutation is intentionally excluded to avoid reset loops
    }, [open]);

    // Close modal on success
    useEffect(() => {
        if (revokeMutation.isSuccess) {
            onOpenChange(false);
        }
    }, [revokeMutation.isSuccess, onOpenChange]);

    const handleConfirm = () => {
        if (!permission) return;

        revokeMutation.mutate({
            role_assignment_uuid: roleAssignmentUuid,
            permission,
        });
    };

    if (!permission) return null;

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-2xl">
                <DialogHeader>
                    <DialogTitle>Revoke Permission</DialogTitle>
                    <DialogDescription>
                        Are you sure you want to revoke this permission from {role}?
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-4">
                    <div className="border rounded-lg p-4 bg-gray-50">
                        <PermissionGrantSummary permission={permission} />
                    </div>

                    <Alert>
                        <AlertDescription>
                            This action cannot be undone. The user will immediately lose access to
                            these resources.
                        </AlertDescription>
                    </Alert>

                    {revokeMutation.error && (
                        <Alert variant="destructive">
                            <AlertDescription>
                                {revokeMutation.error instanceof Error
                                    ? revokeMutation.error.message
                                    : 'Failed to revoke permission'}
                            </AlertDescription>
                        </Alert>
                    )}
                </div>

                <DialogFooter>
                    <Button
                        type="button"
                        variant="outline"
                        onClick={() => onOpenChange(false)}
                        disabled={revokeMutation.isPending}
                    >
                        Cancel
                    </Button>
                    <Button
                        type="button"
                        variant="destructive"
                        onClick={handleConfirm}
                        disabled={revokeMutation.isPending}
                    >
                        {revokeMutation.isPending ? 'Revoking...' : 'Revoke Permission'}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
